export type WhiteboardSaveState =
  'idle' | 'pending' | 'saving' | 'saved' | 'error'

export interface WhiteboardSaverOptions<Scene> {
  send(scene: Scene): Promise<void>
  // Pause without a change before saving (ms)
  delay?: number
  // Waits before each new try after a refused send; the last one repeats
  retryDelays?: number[]
  onStateChange?(state: WhiteboardSaveState): void
}

// Saves the org's whiteboard (#200). The collab server keeps nothing, so the
// database is the only memory: the last version drawn must always end there.
// One send at a time, the latest scene each time, retried until accepted; and
// nothing is sent before the board is loaded, so an empty canvas never
// overwrites a saved one.
export function createWhiteboardSaver<Scene>({
  send,
  delay = 1000,
  retryDelays = [1000, 2000, 5000, 10000, 30000],
  onStateChange,
}: WhiteboardSaverOptions<Scene>) {
  let state: WhiteboardSaveState = 'idle'
  let loaded = false
  let latest: Scene | undefined
  let dirty = false
  let inFlight: Promise<void> | undefined
  let timer: ReturnType<typeof setTimeout> | undefined
  let failures = 0

  function setState(next: WhiteboardSaveState) {
    if (next === state) return
    state = next
    onStateChange?.(next)
  }

  function clearTimer() {
    if (timer === undefined) return
    clearTimeout(timer)
    timer = undefined
  }

  function schedule(ms: number) {
    clearTimer()
    timer = setTimeout(() => {
      timer = undefined
      run()
    }, ms)
  }

  // Sends the latest scene unless a send is already in flight: that one
  // sends again when it ends
  function run(): Promise<void> {
    if (inFlight || !dirty) return inFlight ?? Promise.resolve()
    const scene = latest as Scene
    dirty = false
    setState('saving')
    inFlight = send(scene).then(
      () => {
        inFlight = undefined
        failures = 0
        if (!dirty) return setState('saved')
        setState('pending')
        if (timer === undefined) return run()
      },
      () => {
        inFlight = undefined
        dirty = true
        setState('error')
        schedule(retryDelays[Math.min(failures, retryDelays.length - 1)])
        failures++
      }
    )
    return inFlight
  }

  return {
    // The saved board is on screen: changes may now be sent
    markLoaded() {
      loaded = true
    },
    change(scene: Scene) {
      if (!loaded) return
      latest = scene
      dirty = true
      if (!inFlight && state !== 'error') setState('pending')
      if (state !== 'error') schedule(delay)
    },
    // Sends what is left at once (leaving the view, hiding the tab)
    async flush() {
      clearTimer()
      if (inFlight) await inFlight
      if (dirty) await run()
    },
    hasUnsaved() {
      return dirty || inFlight !== undefined
    },
    getState() {
      return state
    },
  }
}

export type WhiteboardSaver<Scene> = ReturnType<
  typeof createWhiteboardSaver<Scene>
>
